import React, { useState, useEffect, useCallback } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { GoArrowLeft } from "react-icons/go";

export const AdminAthletePaymentTransactions = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState({
    speed: [],
    strength: [],
    tools: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPaymentTransactions = useCallback(async () => {
    if (!staff || !athlete_id) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL.GET_ATHLETE_PURCHASE_DATA_ADMIN}${athlete_id}`,
        {
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      const data = await res.json();

      if (res.ok) {
        setTransactions({
          speed: data.data?.speed || [],
          strength: data.data?.strength || [],
          tools: data.data?.tools || [],
        });
        setError("");
      } else {
        throw new Error(data.error || "Failed to fetch payment transactions.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load transactions. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [athlete_id, staff]);

  useEffect(() => {
    fetchPaymentTransactions();
  }, [fetchPaymentTransactions]);

  // Helper: format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  };

  const renderTransactionList = (list, type) => {
    if (!list || list.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">{type}</h3>
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden bg-white">
          {list.map((tx) => (
            <li
              key={tx._id}
              className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center"
            >
              <div className="flex flex-col sm:flex-row sm:gap-4">
                <p className="text-sm font-semibold">
                  {type === "Tools"
                    ? `Tool: ${tx.title}`
                    : `Template: ${tx.title || tx.template_id}`}
                </p>
                <p className="text-gray-700">Price: ${tx.price}</p>
                <p className="text-gray-700">
                  Transaction ID: {tx.payment_transaction_id}
                </p>
                <p className="text-gray-700">Status: {tx.payment_status}</p>
              </div>
              <div className="mt-2 sm:mt-0 text-gray-500 text-sm">
                Created: {formatDate(tx.createdAt)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasNoTransactions =
    !transactions.speed.length &&
    !transactions.strength.length &&
    !transactions.tools.length;

  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 bg-bgBase overflow-auto p-5 md:p-10">
        <div className="w-full max-w-[1136px] mx-auto">
          <div className="space-y-6 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:ml-12"
            >
              <GoArrowLeft color="#666666" /> Back to Athletes
            </button>

            <div className="flex max-lg:flex-col justify-between lg:items-center gap-5 mb-6">
              <h1 className="font-inter font-medium text-dark text-3xl !leading-none">
                Athlete Payment Transactions
              </h1>

              <div className="flex max-sm:flex-col items-center gap-2">
                <select
                  name=""
                  id=""
                  className="w-full min-w-52 outline-none bg-white border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight capitalize"
                >
                  <option value="">value 1</option>
                  <option value="">value 1</option>
                </select>
                <select
                  name=""
                  id=""
                  className="w-full min-w-52 outline-none bg-white border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight capitalize"
                >
                  <option value="">value 1</option>
                  <option value="">value 1</option>
                </select>
              </div>
            </div>
          </div>

          <div className="w-full max-w-screen-2xl bg-white p-5 md:p-8 rounded-14 mx-auto text-center">
            <h2 className="font-inter font-medium text-dark text-2xl !leading-none mb-4">
              Purchase Transactions
            </h2>

            {loading ? (
              <p className="font-inter font-normal text-sm">
                Loading transactions...
              </p>
            ) : error ? (
              <p className="font-inter font-normal text-sm text-red-500 mb-4">
                {error}
              </p>
            ) : hasNoTransactions ? (
              <p>No transactions available.</p>
            ) : (
              <div className="space-y-6 max-h-[600px] overflow-y-auto">
                {renderTransactionList(transactions.speed, "Speed Templates")}
                {renderTransactionList(
                  transactions.strength,
                  "Strength Templates",
                )}
                {renderTransactionList(transactions.tools, "Tools")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
